import { filter, includes } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';
import { EmptyResponse } from '../data/empty-response.do';
import { MobizGroup, MobizGroupEagerLoad } from '../data/mobiz-group.do';
import { MobizPermission } from '../data/mobiz-permission.do';
import { MobizRole } from '../data/mobiz-role.do';
import { PagedResults } from '../data/paged-results.model';
import { SerializableObjectList } from '../data/serializable-object-list.model';
import { User } from '../data/user.do';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';

import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { FlagsEnum } from '../other/flags-enum';
import { MobizServerApi } from '../other/mobiz-server-api.enum';
import { IAuthTokenService } from './auth-token.interface';
import { GroupQueryParameters, GroupUsersQueryParameters, ISecurityService, RoleQueryParameters } from './security.interface';

@Injectable()
export class SecurityService implements ISecurityService {
  constructor(
    @Inject(IMobizHttpService) private http: IMobizHttpService,
    @Inject(IAuthTokenService) private authToken: IAuthTokenService,
  ) {
  }

  public getAllPermissions(): Observable<MobizPermission[]> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllSecurityPermissions,
    };
    const permissions = new SerializableObjectList<MobizPermission>(MobizPermission);
    return this.http.get(permissions, config).pipe(map((response: SerializableObjectList<MobizPermission>) => {
      return response.items;
    }));
  }

  public getScopedPermissions(organizationId: string): Observable<MobizPermission[]> {
    return this.getAllPermissions()
      .pipe(map((allPermissions: MobizPermission[]) => {
        const permissions = this.authToken.claims.getPermissions(organizationId);
        return filter(allPermissions, p => includes(permissions, p.key));
      }));
  }

  public createRole(role: MobizRole): Observable<MobizRole> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllSecurityRoles,
      body: JSON.stringify(role),
    };
    return this.http.post(new MobizRole(), config);
  }

  public getAllRoles(queryParameters: RoleQueryParameters): Observable<MobizRole[]> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllSecurityRoles,
      queryParameters: queryParameters,
    };
    const roles = new SerializableObjectList<MobizRole>(MobizRole);
    return this.http.get(roles, config).pipe(map((response: SerializableObjectList<MobizRole>) => {
      return response.items;
    }));
  }

  public getRole(roleId: string): Observable<MobizRole> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.SecurityRole,
      urlParameters: {
        roleId: roleId,
      },
    };
    return this.http.get(new MobizRole(), config);
  }

  public updateRole(role: MobizRole): Observable<MobizRole> {
    const config: ApiEndpointConfiguration = {
      body: JSON.stringify(role),
      serverApi: MobizServerApi.SecurityRole,
      urlParameters: {
        roleId: role.id,
      },
    };
    return this.http.put(new MobizRole(), config);
  }

  public deleteRole(roleId: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.SecurityRole,
      urlParameters: {
        roleId: roleId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

  public createGroup(group: MobizGroup): Observable<MobizGroup> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllSecurityGroups,
      body: JSON.stringify(group),
    };
    return this.http.post(new MobizGroup(), config);
  }

  public getAllGroups(queryParameters: GroupQueryParameters, eagerLoad?: FlagsEnum<MobizGroupEagerLoad>)
    : Observable<PagedResults<MobizGroup>> {
    let requiredQueryParameters = queryParameters;
    if (eagerLoad) {
      requiredQueryParameters = { ...queryParameters, eagerLoad: eagerLoad.value };
    }
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllSecurityGroups,
      queryParameters: requiredQueryParameters,
    };
    let pagedUser = new PagedResults<MobizGroup>(MobizGroup);
    return this.http.get(pagedUser, config);
  }

  public getGroup(groupId: string): Observable<MobizGroup> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.SecurityGroup,
      urlParameters: {
        groupId: groupId,
      },
    };
    return this.http.get(new MobizGroup(), config);
  }

  public updateGroup(group: MobizGroup): Observable<MobizGroup> {
    const config: ApiEndpointConfiguration = {
      body: JSON.stringify(group),
      serverApi: MobizServerApi.SecurityGroup,
      urlParameters: {
        groupId: group.id,
      },
    };
    return this.http.put(new MobizGroup(), config);
  }

  public deleteGroup(groupId: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.SecurityGroup,
      urlParameters: {
        groupId: groupId,
      },
    };
    return this.http.delete(new EmptyResponse(), config);
  }

  public getUsersFromGroup(queryParameters: GroupUsersQueryParameters): Observable<PagedResults<User>> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllUsersFromSecurityGroup,
      queryParameters: queryParameters,
      urlParameters: {
        groupId: queryParameters.groupId,
      },
    };
    let pagedUser = new PagedResults<User>(User);
    return this.http.get(pagedUser, config);
  }

  public updateUserGroups(userId: string, groups: MobizGroup[]): Observable<EmptyResponse> {
    const body = JSON.stringify(groups);
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.UserGroup,
      urlParameters: {
        userId: userId,
      },
      body: body,
    };
    return this.http.put(new EmptyResponse(), config);
  }

}
